import React from "react"
import TeamManager from "@/components/TeamManager"
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/react"
import {
  AddIcon,
  ArrowBackIcon,
  ArrowForwardIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@chakra-ui/icons"
import { Api } from "../lib/api"
import { CurrentOrgId, Schemas, SchemaTeams } from "@/lib/store"
import { useParams } from "react-router-dom"
import PermissionsLayoutModule from "@/components/PermissionsLayoutModule"
import AddUserModal from "./AddUserModal"

const MemberListPagination = ({
  offset,
  total,
  limit,
  onPaginationChange,
  addUserButton,
}) => {
  const pageCount = Math.ceil(total / limit)
  const isSinglePage = pageCount <= 1

  const startMember = Math.min(offset + 1, total)
  const endMember = Math.min(offset + limit, total)

  return (
    <PermissionsLayoutModule>
      {!isSinglePage ? (
        <>
          <Button
            size="sm"
            variant="primary"
            onClick={() => onPaginationChange(0)}
            isDisabled={offset === 0}
          >
            <ArrowBackIcon />
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={() => onPaginationChange(offset - limit)}
            isDisabled={offset === 0}
          >
            <ChevronLeftIcon />
          </Button>
          <Text minW="fit-content">{`${startMember}-${endMember}/${total} members`}</Text>
          <Button
            size="sm"
            variant="primary"
            onClick={() => onPaginationChange(offset + limit)}
            isDisabled={offset + limit >= total}
          >
            <ChevronRightIcon />
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={() => onPaginationChange((pageCount - 1) * limit)}
            isDisabled={offset + limit >= total}
          >
            <ArrowForwardIcon />
          </Button>
        </>
      ) : (
        <Text minW="fit-content">{`${total} members`}</Text>
      )}
      {addUserButton}
    </PermissionsLayoutModule>
  )
}

const SchemaPermissions = () => {
  const api = new Api()
  const [total, setTotal] = React.useState(0)
  const [offset, setOffset] = React.useState(0)
  const [limit] = React.useState(50)
  const [orgId] = CurrentOrgId.useState()
  const [members, setMembers] = React.useState([])
  const [isOpen, setIsOpen] = React.useState(false)

  const [schemas] = Schemas.useState()
  const { schemaId } = useParams()
  // Find the selected schema
  const schema = React.useMemo(() => schemas[schemaId], [schemas, schemaId])

  const [teams, setTeams] = SchemaTeams.useState()

  const handleClose = () => {
    setIsOpen(false)
  }

  const handleOpen = () => {
    setIsOpen(true)
  }

  const addUserButton = (
    <Button
      variant="primary"
      rightIcon={<AddIcon />}
      onClick={() => handleOpen()}
    >
      Add User
    </Button>
  )

  const emptyOrg = (
    <Alert status="info" height="fit-content" boxShadow="md">
      <VStack alignItems="flex-start" justifyContent="flex-start" height="100%">
        <HStack>
          <AlertIcon />
          <AlertTitle fontSize="1.25em">No members found</AlertTitle>
        </HStack>
        <AlertDescription fontSize="1em">
          There are no members in your organization available to add to this
          schema. If you would like to add a member to this organization, you
          can do so by clicking the button below.
        </AlertDescription>
        <Box display="flex" justifyContent="flex-end" width="100%">
          {addUserButton}
        </Box>
      </VStack>
    </Alert>
  )

  const refreshTeams = React.useCallback(async () => {
    try {
      const response = await api.schemaTeamsList(schemaId)
      let teamsArray = Array.isArray(response)
        ? response
        : Object.values(response)

      const dataOwnerTeamName = `${schema?.name} Data Owners`
      const sortedTeams = teamsArray.sort((a, b) => {
        if (a.name === dataOwnerTeamName) return -1
        if (b.name === dataOwnerTeamName) return 1
        return 0
      })

      setTeams(sortedTeams)
    } catch (error) {
      console.error("Error fetching teams:", error)
    }
  }, [schemaId])

  React.useEffect(() => {
    if (schemaId) {
      refreshTeams()
    }
  }, [schemaId])

  React.useEffect(() => {
    const getMembers = async () => {
      const response = await api.organizationsUsersList(orgId, limit, offset)
      const parsedUsers = Array.isArray(response)
        ? response.map((user) => user.user)
        : Object.values(response).map((user) => user.user)
      setMembers(parsedUsers)
      setTotal(parsedUsers.length)
    }
    getMembers()
  }, [orgId, limit, offset])

  const formattedMembers = Array.isArray(members)
    ? members.map((member) => ({
        date_joined: member.date_joined,
        email: member.email,
        first_name: member.first_name,
        id: member.id,
        is_active: member.is_active,
        is_superuser: member.is_superuser,
        last_login: member.last_login,
        last_name: member.last_name,
        in_team: [],
      }))
    : Object.values(members).map((member) => ({
        date_joined: member.date_joined,
        email: member.email,
        first_name: member.first_name,
        id: member.id,
        is_active: member.is_active,
        is_superuser: member.is_superuser,
        last_login: member.last_login,
        last_name: member.last_name,
        in_team: [],
      }))

  const formattedTeams = Array.isArray(teams)
    ? teams.map((team) => ({
        id: team.id,
        name: team.name,
        organization: team.organization,
        slug: team.slug,
        url: team.url,
        members: (team.members || []).map((member) => ({
          date_joined: member.date_joined,
          email: member.email,
          first_name: member.first_name,
          id: member.id,
          is_active: member.is_active,
          is_superuser: member.is_superuser,
          last_login: member.last_login,
          last_name: member.last_name,
          in_team: [],
        })),
      }))
    : Object.values(teams).map((team) => ({
        id: team.id,
        name: team.name,
        organization: team.organization,
        slug: team.slug,
        url: team.url,
        members: (team.members || []).map((member) => ({
          date_joined: member.date_joined,
          email: member.email,
          first_name: member.first_name,
          id: member.id,
          is_active: member.is_active,
          is_superuser: member.is_superuser,
          last_login: member.last_login,
          last_name: member.last_name,
          in_team: [],
        })),
      }))

  // Add this useMemo to track team memberships
  const membersWithTeams = React.useMemo(() => {
    return formattedMembers.map((member) => {
      const memberTeams = formattedTeams
        .filter((team) =>
          team.members.some((teamMember) => teamMember.id === member.id),
        )
        .map((team) => team.id)

      return {
        ...member,
        in_team: memberTeams,
      }
    })
  }, [formattedMembers, formattedTeams])

  const handlePaginationChange = (newOffset) => {
    setOffset(newOffset)
  }

  console.log("membersWithTeams", membersWithTeams)
  console.log("formattedTeams", formattedTeams)

  return (
    <>
      <TeamManager
        emptyState={membersWithTeams.length <= 1 ? emptyOrg : null}
        teams={formattedTeams}
        members={membersWithTeams}
        addUserButton={addUserButton}
        membersTopToolbar={
          <MemberListPagination
            offset={offset}
            total={total}
            limit={limit}
            onPaginationChange={handlePaginationChange}
            addUserButton={addUserButton}
          />
        }
        onSuccessfulSaveAndClose={refreshTeams}
      />
      <AddUserModal isOpen={isOpen} onClose={handleClose} />
    </>
  )
}

export default SchemaPermissions
